import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/design-system/Button';
import { Card } from '@/design-system/Card';
import { AlertDialog } from '@/design-system/Dialog';
import { Input } from '@/design-system/Input';
import { useAuth } from '@/core/auth';
import { authService, type TwoFactorSetup } from '@/features/auth';
import { usePlatformBranding } from '@/core/platform-branding';

type Mode = 'password' | 'challenge' | 'enroll' | 'backup';

export function LoginForm() {
  const navigate = useNavigate();
  const { login, completeMfa, completeMfaEnroll } = useAuth();
  const { branding } = usePlatformBranding();

  const [mode, setMode] = useState<Mode>('password');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const [tmpToken, setTmpToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [enrollSetup, setEnrollSetup] = useState<TwoFactorSetup | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const toErrorMessage = (err: unknown, fallback: string) => {
    const m = isAxiosError(err)
      ? (err.response?.data as { message?: string } | undefined)?.message
      : err instanceof Error
        ? err.message
        : undefined;
    return m || fallback;
  };

  const fail = (err: unknown, fallback: string) => {
    setError(toErrorMessage(err, fallback));
    setShowError(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.requires2fa) {
        setTmpToken(result.tmpToken);
        setMode('challenge');
      } else if (result.requires2faSetup) {
        // 2FA obrigatório: inicia o enrollment (gera o QR).
        setTmpToken(result.tmpToken);
        const setup = await authService.enroll2faStart(result.tmpToken);
        setEnrollSetup(setup);
        setMode('enroll');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      fail(err, 'Credenciais inválidas. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await completeMfa(tmpToken!, code.trim());
      navigate('/dashboard');
    } catch (err) {
      fail(err, 'Código inválido. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnrollConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const codes = await completeMfaEnroll(tmpToken!, code.trim());
      setBackupCodes(codes);
      setMode('backup');
    } catch (err) {
      fail(err, 'Código inválido. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const backToPassword = () => {
    setMode('password');
    setTmpToken(null);
    setCode('');
    setEnrollSetup(null);
    setError('');
  };

  const subtitle =
    mode === 'password'
      ? 'Acesse sua conta administrativa'
      : mode === 'challenge'
        ? 'Verificação em duas etapas'
        : 'Configuração obrigatória do 2FA';

  return (
    <>
      <Card className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src={branding.logoUrl || '/orya-dark.png'}
            alt={branding.platformName}
            className="h-12 mx-auto mb-4 object-contain max-w-[200px]"
          />
          <h1 className="text-2xl font-bold text-gray-900">{branding.platformName} Manager</h1>
          <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
        </div>

        {error && mode !== 'backup' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        {mode === 'password' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="E-mail"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              label="Senha"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading}>
              Entrar
            </Button>
          </form>
        )}

        {mode === 'challenge' && (
          <form onSubmit={handleVerify2fa} className="space-y-4">
            <p className="text-sm text-gray-600">
              Informe o código de 6 dígitos do seu app autenticador (ou um backup code).
            </p>
            <Input
              label="Código"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading}>
              Verificar
            </Button>
            <Button type="button" variant="ghost" size="sm" className="w-full text-gray-500 hover:text-gray-700" onClick={backToPassword}>
              Voltar
            </Button>
          </form>
        )}

        {mode === 'enroll' && enrollSetup && (
          <form onSubmit={handleEnrollConfirm} className="space-y-3">
            <p className="text-sm text-gray-700">
              Sua conta exige verificação em duas etapas. Escaneie o QR no seu app autenticador:
            </p>
            <img src={enrollSetup.qrCodeDataUrl} alt="QR Code 2FA" className="w-40 h-40 mx-auto border rounded-lg" />
            <p className="text-xs text-gray-500 break-all text-center">
              Chave manual: <span className="font-mono">{enrollSetup.secret}</span>
            </p>
            <Input
              label="Código do app"
              type="text"
              inputMode="numeric"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isLoading} disabled={code.length < 6}>
              Confirmar e entrar
            </Button>
            <Button type="button" variant="ghost" size="sm" className="w-full text-gray-500 hover:text-gray-700" onClick={backToPassword}>
              Cancelar
            </Button>
          </form>
        )}

        {mode === 'backup' && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-amber-900">
              2FA ativado! Guarde seus backup codes (mostrados só uma vez):
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg grid grid-cols-2 gap-1 font-mono text-sm text-amber-900">
              {backupCodes.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            <Button variant="primary" size="lg" className="w-full" onClick={() => navigate('/dashboard')}>
              Já salvei — continuar
            </Button>
          </div>
        )}
      </Card>

      <AlertDialog
        isOpen={showError}
        onClose={() => setShowError(false)}
        title="Erro no Login"
        message={error}
        variant="error"
      />
    </>
  );
}
